import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const WelcomePage = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Deteksi perangkat iOS
  const detectIOS = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };

  // Cek apakah sudah dalam mode standalone (sudah terinstall)
  const checkStandalone = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    );
  };

  useEffect(() => {
    const iOS = detectIOS();
    const standalone = checkStandalone();

    setIsIOS(iOS);
    setIsStandalone(standalone);

    // Jika iOS dan belum terinstall, tampilkan modal
    if (iOS && !standalone) {
      console.log("iOS detected - showing install modal");
      setShowInstallModal(true);
    }

    // Handler untuk Android (beforeinstallprompt)
    const handler = (e: any) => {
      e.preventDefault();
      console.log("beforeinstallprompt event fired - Android");
      setDeferredPrompt(e);
      setShowInstallModal(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Cek apakah sudah terinstall di Android
    if (standalone && !iOS) {
      console.log("App sudah terinstall di Android");
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Install untuk Android
  const handleInstallAndroid = async () => {
    if (!deferredPrompt) {
      console.log("Deferred prompt tidak tersedia");
      return;
    }

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === "accepted") {
      console.log("User menerima instalasi Android");
    } else {
      console.log("User menolak instalasi Android");
    }

    setDeferredPrompt(null);
    setShowInstallModal(false);
  };

  // Install untuk iOS (informasi manual)
  const handleInstallIOS = () => {
    // iOS tidak bisa install otomatis, jadi kita tutup modal
    // User sudah diberi instruksi di modal
    setShowInstallModal(false);
    console.log("User menutup instruksi instalasi iOS");
  };

  const handleSkipInstall = () => {
    setShowInstallModal(false);
    console.log("User melewati instalasi");
  };

  const handleGetStarted = () => {
    navigate("/home");
  };

  return (
    <div className="h-screen -mt-20 md:mt-0 overflow-hidden relative bg-gradient-to-br from-[#efecfa] via-[#ffffff] to-[#d7d0fe]">
      {/* Background elements - lebih sederhana */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-r from-[#d7d0fe] to-[#ffecba] opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-gradient-to-r from-[#ffecba] to-[#efecfa] opacity-20 blur-3xl"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center h-full px-4">
        <div className="w-full max-w-6xl">
          {/* Konten utama - semua dalam satu layar */}
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:gap-12">
            {/* Left side - Teks dan tombol */}
            <div className="lg:w-1/2">
              <h1 className="mb-4 text-4xl font-bold leading-tight md:text-5xl">
                <span className="block text-[#1e1e1e]">Solusi Kasir</span>
                <span className="block text-[#37393d] bg-clip-text">
                  Modern & Cepat
                </span>
              </h1>

              <p className="text-lg text-[#37393d] mb-6 leading-relaxed">
                <span className="font-semibold text-[#1e1e1e]">
                  Sellerku POS
                </span>{" "}
                adalah aplikasi kasir yang dirancang khusus untuk membantu Anda
                mengelola transaksi dengan mudah dan efisien.
              </p>

              {/* Tombol utama */}
              <button
                onClick={handleGetStarted}
                className="cursor-pointer group relative px-8 py-4 bg-[#37393d] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 w-full md:w-auto"
              >
                <span className="relative flex items-center justify-center text-white">
                  Mulai Menggunakan POS
                  <svg
                    className="w-5 h-5 ml-3 transition-transform duration-300 transform group-hover:translate-x-1"
                    fill="none"
                    stroke="#fff"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </button>

              {/* Info tambahan kecil */}
              <div className="flex items-center justify-center mt-6 md:justify-start">
                <div className="w-8 h-8 rounded-full bg-[#efecfa] flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-[#37393d]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-[#37393d]">
                  <span className="font-semibold">Aman & Terpercaya</span> •
                  Digunakan 10.000+ bisnis
                </p>
              </div>
            </div>

            {/* Right side - Visual mockup */}
            <div className="absolute flex justify-center scale-125 lg:w-1/2 -z-10 opacity-40 rotate-12">
              <div className="relative w-full max-w-md">
                {/* Main mockup card */}
                <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-[#efecfa]">
                  {/* Header mockup */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#d7d0fe] to-[#ffecba]"></div>
                      <div className="ml-3">
                        <div className="w-20 h-3 bg-[#efecff] rounded-full"></div>
                        <div className="w-16 h-2 bg-[#efecff] rounded-full mt-1"></div>
                      </div>
                    </div>
                    <div className="w-12 h-6 bg-gradient-to-r from-[#ffecba] to-[#ff7075] rounded-lg"></div>
                  </div>

                  {/* Transaction section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-24 h-4 bg-[#efecff] rounded-full"></div>
                      <div className="w-16 h-6 bg-[#d7d0fe] rounded-lg"></div>
                    </div>

                    {/* Product items */}
                    <div className="mb-6 space-y-3">
                      {[1, 2, 3].map((item) => (
                        <div
                          key={item}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-[#efecfa] rounded-lg"></div>
                            <div className="ml-3">
                              <div className="w-24 h-3 bg-[#efecff] rounded-full"></div>
                              <div className="w-16 h-2 bg-[#efecff] rounded-full mt-1"></div>
                            </div>
                          </div>
                          <div className="w-12 h-6 bg-[#ffecba] rounded-lg"></div>
                        </div>
                      ))}
                    </div>

                    {/* Total section */}
                    <div className="pt-4 border-t border-[#efecfa]">
                      <div className="flex items-center justify-between">
                        <div className="w-16 h-4 bg-[#37393d] rounded-full"></div>
                        <div className="w-20 h-6 bg-gradient-to-r from-[#ff7075] to-[#d7d0fe] rounded-lg"></div>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-10 bg-[#efecfa] rounded-lg"></div>
                    <div className="h-10 bg-gradient-to-r from-[#d7d0fe] to-[#ff7075] rounded-lg"></div>
                  </div>
                </div>

                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-xl bg-gradient-to-br from-[#ffecba] to-[#ffffff] opacity-80 shadow-lg rotate-6"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-gradient-to-br from-[#d7d0fe] to-[#efecfa] opacity-70 shadow-lg -rotate-6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Install PWA - Android */}
      {showInstallModal && !isIOS && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-11/12 max-w-md p-6 text-center bg-white shadow-2xl rounded-2xl animate-scale-in">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#d7d0fe] to-[#ffecba] flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </div>
            </div>

            <h2 className="mb-3 text-2xl font-bold text-[#1e1e1e]">
              Pasang Aplikasi Ini?
            </h2>
            <p className="mb-6 text-[#37393d] leading-relaxed">
              Tambahkan <span className="font-semibold">Sellerku POS</span> ke
              perangkat Anda untuk akses lebih cepat dan pengalaman seperti
              aplikasi native.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleSkipInstall}
                className="px-6 py-3 text-[#37393d] transition border-2 border-gray-300 rounded-xl hover:bg-gray-50 font-semibold"
              >
                Nanti Saja
              </button>
              <button
                onClick={handleInstallAndroid}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#37393d] to-[#1e1e1e] text-white hover:shadow-lg transition-all duration-300 font-semibold transform hover:scale-105"
              >
                Pasang Sekarang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Install PWA - iOS */}
      {showInstallModal && isIOS && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center">
          <div className="w-full max-w-md p-6 bg-white shadow-2xl sm:rounded-2xl rounded-t-2xl animate-slide-up sm:animate-scale-in">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#d7d0fe] to-[#ffecba] flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <h2 className="mb-3 text-2xl font-bold text-center text-[#1e1e1e]">
              Pasang Sellerku POS
            </h2>
            <p className="mb-6 text-center text-[#37393d] leading-relaxed">
              Install aplikasi ini di iPhone Anda untuk pengalaman terbaik
            </p>

            {/* Instruksi iOS */}
            <div className="mb-6 space-y-4">
              <div className="flex items-start p-4 bg-[#efecfa] rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-sm font-bold text-[#37393d]">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#37393d]">
                    Tap tombol <span className="font-semibold">Share</span>
                    <svg
                      className="inline w-4 h-4 mx-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                    </svg>
                    di Safari
                  </p>
                </div>
              </div>

              <div className="flex items-start p-4 bg-[#efecfa] rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-sm font-bold text-[#37393d]">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#37393d]">
                    Pilih{" "}
                    <span className="font-semibold">"Add to Home Screen"</span>
                    <svg
                      className="inline w-4 h-4 mx-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </p>
                </div>
              </div>

              <div className="flex items-start p-4 bg-[#efecfa] rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-sm font-bold text-[#37393d]">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#37393d]">
                    Tap <span className="font-semibold">"Add"</span> untuk
                    menambahkan ke Home Screen
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleInstallIOS}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#37393d] to-[#1e1e1e] text-white hover:shadow-lg transition-all duration-300 font-semibold"
              >
                Saya Mengerti
              </button>
              <button
                onClick={handleSkipInstall}
                className="px-6 py-3 text-[#37393d] transition hover:bg-gray-50 font-semibold rounded-xl"
              >
                Nanti Saja
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomePage;
